import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import sparkles from './assets/stars.svg';
import { languages, languageCodes, languageQuestions } from './constants';

const SearchBox = () => {
    const [query, setQuery] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState("English");
    const [placeholder, setPlaceholder] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isMicActive, setIsMicActive] = useState(false);
    const [enableMic, setEnableMic] = useState(false);
    const [isUsingMediaRecorder, setIsUsingMediaRecorder] = useState(false);
    const urlParams = new URLSearchParams(window.location.search);
    const websiteRef = urlParams.get("websiteRef");
    const parentDivRef = useRef(null);
    const recognitionRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const hasFetchedRef = useRef(false);

    // Function to convert WebM to WAV
    async function convertToWavBlob(blob) {
        const arrayBuffer = await blob.arrayBuffer();
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const numChannels = audioBuffer.numberOfChannels;
        const sampleRate = audioBuffer.sampleRate;
        const length = audioBuffer.length * numChannels * 2;
        const wavBuffer = new ArrayBuffer(44 + length);
        const view = new DataView(wavBuffer);

        function writeString(view, offset, str) {
            for (let i = 0; i < str.length; i++) {
                view.setUint8(offset + i, str.charCodeAt(i));
            }
        }

        let offset = 0;
        writeString(view, offset, 'RIFF'); offset += 4;
        view.setUint32(offset, 36 + length, true); offset += 4;
        writeString(view, offset, 'WAVE'); offset += 4;
        writeString(view, offset, 'fmt '); offset += 4;
        view.setUint32(offset, 16, true); offset += 4;
        view.setUint16(offset, 1, true); offset += 2;
        view.setUint16(offset, numChannels, true); offset += 2;
        view.setUint32(offset, sampleRate, true); offset += 4;
        view.setUint32(offset, sampleRate * numChannels * 2, true); offset += 4;
        view.setUint16(offset, numChannels * 2, true); offset += 2;
        view.setUint16(offset, 16, true); offset += 2;
        writeString(view, offset, 'data'); offset += 4;
        view.setUint32(offset, length, true); offset += 4;

        for (let i = 0; i < audioBuffer.length; i++) {
            for (let channel = 0; channel < numChannels; channel++) {
                const sample = audioBuffer.getChannelData(channel)[i];
                const s = Math.max(-1, Math.min(1, sample));
                view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
                offset += 2;
            }
        }
        return new Blob([view], { type: 'audio/wav' });
    }

    // Handle search
    const handleSearch = () => {
        if (!query.trim()) return;

        const searchParams = {
            query,
            websiteRef,
            selected_language: selectedLanguage
        };

        window.parent.postMessage({
            type: 'searchParameters',
            data: searchParams
        }, '*');
    };

    // Handle key down event for input
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    // Fetch AI search configuration
    useEffect(() => {
        const fetchAISearchConfig = async () => {
            if (hasFetchedRef.current) {
                console.log('Fetch skipped: Already fetched configuration');
                return;
            }

            console.log('Fetching AI search configuration for websiteRef:', websiteRef, 'and language:', selectedLanguage);
            hasFetchedRef.current = true;

            if (!websiteRef) {
                setPlaceholder(languageQuestions[selectedLanguage]);
                setEnableMic(true);
                setIsLoading(false);
                return;
            }

            try {
                const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/website/get_ai_search_configurations/${websiteRef}`);
                const data = await response.json();

                console.log('AI Search Config:', data);

                if (data.site_config === true && data.ai_search_welcome_message) {
                    const plainTextMessage = data.ai_search_welcome_message.replace(/<[^>]*>/g, '');
                    setPlaceholder(plainTextMessage);
                } else {
                    setPlaceholder(languageQuestions[selectedLanguage]);
                }

                setEnableMic(data.ai_search_enable_mic === "True" ? true : false);
            } catch (error) {
                console.error('Error fetching AI search configuration:', error);
                setPlaceholder(languageQuestions[selectedLanguage]);
                setEnableMic(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAISearchConfig();
    }, [websiteRef, selectedLanguage]);

    // Listen for language updates from parent
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data.type === 'languageChange') {
                console.log('Received languageChange event:', event.data.language);
                setSelectedLanguage(event.data.language);
                hasFetchedRef.current = false; // Allow fetch on language change
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    // Send height to parent and initial constants
    useEffect(() => {
        const sendHeightToParent = () => {
            if (parentDivRef.current) {
                const height = parentDivRef.current.getBoundingClientRect().height;
                window.parent.postMessage({
                    type: 'searchBoxHeight',
                    height: height
                }, '*');
            }
        };

        const observer = new ResizeObserver(sendHeightToParent);

        if (parentDivRef.current) {
            observer.observe(parentDivRef.current);
            sendHeightToParent();
        }

        const sendInitialConstants = () => {
            window.parent.postMessage({
                type: 'initialConstants',
                constants: {
                    languages: languages,
                    languageCodes: languageCodes,
                    languageQuestions: languageQuestions
                }
            }, '*');
        };

        sendInitialConstants();

        return () => {
            observer.disconnect();
        };
    }, []);

    // Toggle microphone
    const toggleMic = async () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (isMicActive) {
            if (isUsingMediaRecorder && mediaRecorderRef.current) {
                mediaRecorderRef.current.stop();
            } else if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            setIsMicActive(false);
            return;
        }

        if (SpeechRecognition) {
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = languageCodes[selectedLanguage] || 'en-US';

            let finalTranscript = '';

            recognitionRef.current.onresult = (event) => {
                let interimTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + ' ';
                    } else {
                        interimTranscript += transcript;
                    }
                }
                setQuery(finalTranscript + interimTranscript);
            };

            recognitionRef.current.onerror = (event) => {
                console.error('SpeechRecognition error:', event.error);
                setIsMicActive(false);
                window.parent.postMessage({
                    type: 'searchError',
                    message: 'Speech recognition failed. Please try again or type your query.'
                }, '*');
            };

            recognitionRef.current.onend = () => {
                if (isMicActive) {
                    recognitionRef.current.start();
                } else {
                    setIsMicActive(false);
                }
            };

            recognitionRef.current.start();
            setIsMicActive(true);
            setIsUsingMediaRecorder(false);
        } else {
            if (!window.isSecureContext) {
                window.parent.postMessage({
                    type: 'searchError',
                    message: 'Speech to text requires HTTPS.'
                }, '*');
                return;
            }
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                mediaRecorderRef.current = new MediaRecorder(stream);
                audioChunksRef.current = [];

                mediaRecorderRef.current.ondataavailable = (e) => {
                    audioChunksRef.current.push(e.data);
                };

                mediaRecorderRef.current.onstop = async () => {
                    setQuery("Transcribing...");
                    const webmBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                    const wavBlob = await convertToWavBlob(webmBlob);

                    const formData = new FormData();
                    formData.append('audio', wavBlob, 'recording.wav');

                    try {
                        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/transcribe`, {
                            method: 'POST',
                            body: formData
                        });
                        const data = await response.json();
                        setQuery(data.text || "");
                    } catch (err) {
                        window.parent.postMessage({
                            type: 'searchError',
                            message: 'Transcription failed: ' + err.message
                        }, '*');
                    }
                    setIsMicActive(false);
                };

                mediaRecorderRef.current.start();
                setIsMicActive(true);
                setIsUsingMediaRecorder(true);
            } catch (err) {
                window.parent.postMessage({
                    type: 'searchError',
                    message: 'Microphone access denied or unavailable.'
                }, '*');
            }
        }
    };

    return (
        <div className="relative w-full" ref={parentDivRef}>
            <div className="relative p-[2px] rounded-lg">
                <div className="absolute inset-0 rounded-lg overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#4900ff] via-[#a166ab] to-[#000000] animate-gradient"></div>
                </div>
                <div className="relative flex items-center bg-white rounded-lg shadow-sm">
                    <img className="absolute left-3 h-4 w-4 z-10" src={sparkles} alt="sparkles" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={isLoading ? 'Loading...' : placeholder}
                        className="w-full pl-10 pr-[100px] py-3 rounded-l-lg focus:outline-none"
                        disabled={isLoading}
                    />
                    <div className="absolute inset-y-0 right-[110px] flex items-center" style={{ zIndex: 20 }}>
                        {enableMic && (
                            <button
                                type="button"
                                onClick={toggleMic}
                                className={`flex items-center justify-center px-2 text-sm rounded hover:bg-gray-100 border ${isMicActive ? 'animate-pulse bg-red-100' : ''}`}
                                style={{
                                    minWidth: "32px",
                                    height: "32px",
                                    borderColor: "#e5e7eb"
                                }}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path>
                                </svg>
                            </button>
                        )}
                    </div>
                    <button
                        onClick={handleSearch}
                        disabled={isLoading}
                        className="flex items-center justify-center px-4 py-3 text-white bg-[#4900ff] hover:bg-blue-600 transition-colors border border-gray-300 rounded-r-lg mr-1 my-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Search size={20} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SearchBox;